import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Edit, ExternalLink, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DataTable, Column } from "@/components/ui/data-table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { MonitoringServer } from "@/types/monitoring";
import {
  useMonitoringServers,
  useDeleteServer,
  useServerHealth,
} from "@/hooks/useMonitoring";
import { ServerFormDialog } from "./ServerFormDialog";
import { usePlatformPermissions } from "@/hooks/usePlatformPermissions";

// --- Health badge ---

interface HealthBadgeProps {
  serverId: string;
}

const HealthBadge: React.FC<HealthBadgeProps> = ({ serverId }) => {
  const { t } = useTranslation();
  const { data: health, isLoading } = useServerHealth(serverId);

  if (isLoading) {
    return (
      <Badge variant="new_secondary">{t("monitoring.servers.checking")}</Badge>
    );
  }

  return health?.reachable ? (
    <Badge variant="verified">{t("monitoring.servers.connected")}</Badge>
  ) : (
    <Badge variant="new_destructive">
      {t("monitoring.servers.unreachable")}
    </Badge>
  );
};

// --- Main component ---

export const ServersTab: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { canAdminister } = usePlatformPermissions();
  const canWrite = canAdminister("monitoring");
  const { data: servers, isLoading } = useMonitoringServers();
  const { mutate: deleteServer, isPending: isDeleting } = useDeleteServer();

  const [formOpen, setFormOpen] = useState(false);
  const [editTarget, setEditTarget] = useState<MonitoringServer | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<MonitoringServer | null>(
    null,
  );

  const handleEdit = (server: MonitoringServer) => {
    setEditTarget(server);
    setFormOpen(true);
  };

  const handleDelete = () => {
    if (!deleteTarget) return;
    deleteServer(deleteTarget.id, {
      onSuccess: () => {
        toast.success(t("monitoring.servers.deleteSuccess"));
        setDeleteTarget(null);
      },
      onError: () => toast.error(t("monitoring.servers.deleteError")),
    });
  };

  const columns: Column<MonitoringServer>[] = [
    {
      key: "name",
      header: t("monitoring.servers.name"),
      render: (value) => <span className="font-medium">{value}</span>,
    },
    {
      key: "url",
      header: t("monitoring.servers.url"),
      render: (value) => (
        <span className="text-muted-foreground text-xs">{value}</span>
      ),
    },
    {
      key: "metricRateSeconds",
      header: t("monitoring.servers.metricsRate"),
      render: (value) => (
        <span className="text-xs">
          {t("monitoring.servers.everyNSeconds", { count: value })}
        </span>
      ),
    },
    {
      key: "metricHistoryDays",
      header: t("monitoring.servers.history"),
      render: (value) => (
        <span className="text-xs">
          {t("monitoring.servers.nDays", { count: value })}
        </span>
      ),
    },
    {
      key: "id",
      header: t("monitoring.servers.status"),
      render: (_, row) => <HealthBadge serverId={row.id} />,
    },
    {
      key: "actions",
      header: t("monitoring.servers.actions"),
      className: "text-center",
      render: (_, row) => (
        <div className="flex items-center justify-center gap-1">
          <Button
            variant="ghost"
            size="icon"
            title={t("monitoring.servers.dashboard")}
            aria-label={t("monitoring.servers.dashboard")}
            onClick={() => navigate(`/monitoring/${row.id}`)}
          >
            <ExternalLink className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title={t("monitoring.servers.editServer")}
            aria-label={t("monitoring.servers.editServer")}
            disabled={!canWrite}
            onClick={() => handleEdit(row)}
          >
            <Edit className="size-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            title={t("monitoring.servers.delete")}
            aria-label={t("monitoring.servers.delete")}
            disabled={!canWrite}
            onClick={() => setDeleteTarget(row)}
          >
            <Trash2 className="size-4" />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">
          {t("monitoring.servers.title")}
        </h2>
        <Button
          variant="new_default"
          size="default"
          className="gap-2"
          disabled={!canWrite}
          onClick={() => {
            setEditTarget(null);
            setFormOpen(true);
          }}
        >
          <Plus className="size-4" />
          {t("monitoring.servers.addServer")}
        </Button>
      </div>

      <DataTable<MonitoringServer>
        data={servers ?? []}
        columns={columns}
        loading={isLoading}
        withPagination={false}
        withSorting={false}
        emptyMessage={t("monitoring.servers.empty")}
      />

      {/* Add/Edit dialog */}
      <ServerFormDialog
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) setEditTarget(null);
        }}
        server={editTarget}
      />

      {/* Delete confirmation */}
      <Dialog
        open={!!deleteTarget}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t("monitoring.servers.deleteConfirm", {
                name: deleteTarget?.name,
              })}
            </DialogTitle>
            <DialogDescription />
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteTarget(null)}
              disabled={isDeleting}
            >
              {t("common.cancel")}
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
            >
              {t("monitoring.servers.delete")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
